// @ts-nocheck
// import StyledWrapper from "@insite/client-framework/Common/StyledWrapper"; // Valtech Custom Code
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandProductLinesStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code */
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { categoryListProps } from "@store/Themes/common/props/category";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";

const StyledSpan = getStyledWrapper("span");
/* Valtech Custom Code */

const enum fields {
    title = "title",
    showImages = "showImages",
    maxToShow = "maxToShow",
    gridType = "gridType",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImages]: boolean;
        [fields.maxToShow]: number;
        [fields.gridType]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandDetailsProductLinesStyles {
    titleItem?: GridItemProps;
    title?: TypographyProps;
    container?: InjectableCss;
    productLineContainer?: GridContainerProps;
    productLineItemRow?: GridItemProps;
    productLineItemColumn?: GridItemProps;
    innerProductLineContainer?: GridContainerProps;
    productLineImageItem?: GridItemProps;
    productLineImage?: LazyImageProps;
    /** @deprecated item is no longer used for syntatic correctness */
    productLineNameLinkItem?: GridItemProps;
    productLineNameItem?: GridItemProps;
    productLineNameLink?: LinkProps;
    productLineName?: TypographyProps;
    viewAllButtonItem?: GridItemProps;
    viewAllButton?: ButtonProps;
    /** @deprecated Clickable component is no longer used for syntatic correctness */
    productLineImageClickable?: ClickablePresentationProps;
    // Valtech Custom Code
    loadingOverlay?: LoadingOverlayProps;
    noResult?: TypographyProps;
    productLineItem?: GridItemProps;
    productLineLink?: LinkProps;
    productLineImageWrapper?: InjectableCss;
    productLineTitle?: TypographyProps;
}

export const productLinesStyles: BrandDetailsProductLinesStyles = {
    title: categoryListProps.title,
    loadingOverlay: categoryListProps.loadingOverlay,
    noResult: categoryListProps.noResult,
    productLineContainer: categoryListProps.gridContainer,
    productLineItem: categoryListProps.gridItem,
    productLineLink: categoryListProps.itemLink,
    productLineImageWrapper: categoryListProps.itemImageWrapper,
    productLineImage: categoryListProps.itemImage,
    productLineTitle: categoryListProps.itemTitle,
    viewAllButtonItem: categoryListProps.moreGridItem,
    viewAllButton: categoryListProps.moreButton,
};

const styles = productLinesStyles;

const BrandDetailsProductLines: FC<Props> = ({ fields }) => {
    const brandProductLinesDataView = usePersistentDataView(useContext(BrandProductLinesStateContext)); // Valtech Custom Code
    const { isLoading, value: brandProductLines } = brandProductLinesDataView; // Valtech Custom Code
    const { title, showImages, gridType, maxToShow } = fields;
    const productLineItemStyles = gridType === "row" ? styles.productLineItemRow : styles.productLineItemColumn;
    const [viewCount, setViewCount] = React.useState(maxToShow);
    useEffect(() => {
        setViewCount(maxToShow);
    }, [maxToShow]);
    // Valtech Removed - return null;

    const handleShowAllClicked = () => {
        setViewCount(brandProductLines?.length || maxToShow);
    };
    const renderShowAllLink = () => {
        if (maxToShow === 0 || (brandProductLines || []).length <= viewCount) {
            return null;
        }
        return (
            <GridItem {...styles.viewAllButtonItem}>
                <Button
                    onClick={handleShowAllClicked}
                    {...styles.viewAllButton}
                    data-test-selector="brandProductLineShowAllLink"
                >
                    {translate("Show All Product Lines") /* Valtech Custom Code */}
                </Button>
            </GridItem>
        );
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandProductLines">
            {/* Valtech Custom Code */}
            <Typography {...styles.title} data-test-selector="brandProductLinesTitle">
                {title}
            </Typography>
            <DataViewLoadingOverlay dataView={brandProductLinesDataView} {...styles.loadingOverlay}>
                {brandProductLines?.length ? (
                    <GridContainer {...styles.productLineContainer}>
                        {brandProductLines?.slice(0, viewCount).map(productLine => (
                            <GridItem key={productLine.id} {...styles.productLineItem}>
                                <Link href={productLine.productListPagePath} {...styles.productLineLink}>
                                    {showImages && (
                                        <StyledSpan {...styles.productLineImageWrapper}>
                                            <LazyImage
                                                src={productLine.featuredImagePath}
                                                altText={productLine.featuredImageAltText || productLine.name}
                                                {...styles.productLineImage}
                                                data-test-selector={`brandProductLineImage_${productLine.id}`}
                                            />
                                        </StyledSpan>
                                    )}
                                    <Typography
                                        {...styles.productLineTitle}
                                        data-test-selector={`brandProductLineLink_${productLine.id}`}
                                    >
                                        {productLine.name}
                                    </Typography>
                                </Link>
                            </GridItem>
                        ))}
                        {renderShowAllLink()}
                    </GridContainer>
                ) : (
                    <Typography {...styles.noResult}>
                        {translate("There is no product line associated to this brand.")}
                    </Typography>
                )}
            </DataViewLoadingOverlay>
            {/* Valtech Custom Code */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsProductLines),
    definition: {
        group: "Brand Details",
        displayName: "Brand Product Lines",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Product Lines",
                fieldType: "Translatable",
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.maxToShow,
                displayName: "Max To Show",
                editorTemplate: "IntegerField",
                defaultValue: 8,
                fieldType: "General",
            },
            // Valtech Removed - fields.gridType
        ],
    },
};

export default widgetModule;
